import dayjs from "./dayjs";

export const permissionArray: string[] = [
  "add user",
  "get user",
  "get user list",
  "update user",
  "delete user",
  "add role",
  "get role",
  "get role list",
  "update role",
  "delete role",
  "delete permission",
  "add permission",
  "get permission",
  "get permission list",
  "get role permission list",
  "add role permission list",
  "get role permission",
  "update role permission",
  "delete role permission",
  "get category",
  "delete category",
  "get category list",
  "add category list",
  "update category",
  "update product",
  "add product",
  "get product",
  "get product list",
  "delete product",
  "delete product image",
  "get product image",
  "get product image list",
  "add product image",
  "update product image",
  "create single sub-category",
  "fetch single sub-category",
  "update single sub-category",
  "get sub-category list",
  "delete sub-category",
  "create single vendor",
  "delete single vendor",
  "get single vendor",
  "get vendor list",
  "update vendor list",
  "get dashboard analytics",
  "get top product",
  "get near expiry product",
  "get sales report",
  "update campaign status",
  "update vendor status",
  "get order list",
  "update order status",
  "update order payment status",
  "update order delivery status",
  "get delivery status of order",
];

export const saveToken = (token: string, remember: boolean) => {
  if (remember) localStorage.setItem("access_token", token);
  else sessionStorage.setItem("access_token", token);
};

export const getToken = (): string | null => {
  return (
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token")
  );
};

interface UserData {
  email?: string;
  userId?: string;
  userName?: string;
  Permissions?: string[];
  [key: string]: any;
}
export const getCurrentUser = (key?: keyof UserData) => {
  const data = JSON.parse(localStorage.getItem("user") || "{}");
  if (!key) return data;
  return data[key];
};

export const clearOnLogout = () => {
  localStorage.removeItem("access_token");
  sessionStorage.removeItem("access_token");
  localStorage.removeItem("user");
};

export const combineClasses = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(" ");

export const env = (key: keyof ImportMetaEnv, defaultValue = ""): string => {
  const value = import.meta.env[key];
  return typeof value === "string" ? value : defaultValue;
};

export const requiredMessage = (name: string) => `${name} is required!`;

export const onlyNumbersInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (["e", "E", "+", "-", "."].includes(e.key)) {
    e.preventDefault();
  }
};

export const capitalizeText = (text?: any) => {
  if (typeof text !== "string") return "";

  return text
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const CURRENCY_SYMBOL = "₹";

export const getInitialValues = (config: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};

  const traverse = (obj: Record<string, any>) => {
    Object.entries(obj).forEach(([key, value]) => {
      // If it's a field config (has defaultValue)
      if (value && typeof value === "object" && "defaultValue" in value) {
        result[key] = value.defaultValue;
      }
      // If it's a nested object (section/group)
      else if (value && typeof value === "object") {
        traverse(value);
      }
    });
  };

  traverse(config);
  return result;
};

export const getSelectOptions = (arr?: { label: any; value: any }[]) =>
  (arr ?? []).map((item) => ({
    label: String(item.label),
    value: String(item.value),
  }));

export const createEnum = (obj: Record<string, any>) => {
  Object.freeze(obj);
};

export const dateConverter = (str: string | Date) => {
  const d = new Date(str);
  let mm = d.getMonth() + 1;
  let dd = d.getDate();

  return [
    (dd > 9 ? "" : "0") + dd,
    (mm > 9 ? "" : "0") + mm,
    d.getFullYear(),
  ].join("-");
};

export const isoDateIntoString = (isoDate: string): string => {
  if (!isoDate) return "";

  const date = new Date(isoDate);

  // Format: "02 October 2025"
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-GB", { month: "long" });
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const extractTime = (isoString: string): string => {
  if (!isoString) return "";
  return dayjs(isoString).local().format("h:mm A");
};

//Input 16:10 Output: 04:10PM
export const formatTime12Hour = (time: string): string => {
  if (!time) return "";

  return dayjs(time).local().format("hh:mm A");
};

// Output: "06 November 2025"
export const formatDateToReadable = (date: string): string => {
  if (!date) return "";
  return dayjs(date).local().format("DD MMM YYYY");
};

// Output: "Jan 02, 2026"
export const formatDate = (date: string): string => {
  if (!date) return "";
  return dayjs(date).local().format("MMM DD, YYYY");
};

export const dateTimeConverter = (
  isoDate: string,
  withTime = false,
): string => {
  if (!isoDate) return "";

  const format = withTime ? "DD MMMM YYYY - HH:mm" : "DD MMMM YYYY";

  return dayjs(isoDate).local().format(format);
};

export const getIp = (str: string) => {
  if (!str) return "";
  return str.replace(/^::ffff:/, "").split(":")[0];
};

export const inputDateFormat = (date?: string | Date) =>
  date ? new Date(date).toISOString().split("T")[0] : "";

export const updateParams = (
  params: Record<string, any>,
  setParams: React.Dispatch<React.SetStateAction<Map<string, any>>>,
) => {
  setParams((prev) => {
    Object.entries(params || {}).forEach(([key, value]) => {
      prev.set(key, value);
    });
    return prev;
  });
};

export const addBaseUrl = (url?: string) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:")) {
    return url;
  }
  return `${env("VITE_API_BASE_URL")}/${url}`;
};

export const checkPermission = (permission?: string) => {
  if (!permission) return true; // no permission required = always allowed
  const permissions: string[] = getCurrentUser("permissions") || [];
  return permissions.includes(permission.toLowerCase());
};

/** Check if the user has ANY permission for a given module (e.g. "banner", "events") */
export const checkModuleAccess = (moduleName?: string) => {
  if (!moduleName) return true;
  const permissions: string[] = getCurrentUser("permissions") || [];
  return permissions.some((p) =>
    p.toLowerCase().startsWith(`${moduleName.toLowerCase()}:`)
  );
};

/** Check if the current user has a specific role (e.g. "SUPER_ADMIN") */
export const checkRole = (roleName?: string) => {
  if (!roleName) return true; // no role required = always allowed
  const user = getCurrentUser();
  const userRole = user?.Role?.name || "";
  return userRole.toUpperCase() === roleName.toUpperCase();
};

export const conditionalColumns = <T>(
  condition: boolean,
  columns: T[] | T,
): T[] => {
  const colArray = Array.isArray(columns) ? columns : [columns];
  return condition ? colArray : [];
};

export const formatNumberWithSuffix = (
  value: number,
  roundDigits = 1,
): string => {
  if (typeof value !== "number" || Number.isNaN(value)) return "0";

  // Handle negative numbers
  const isNegative = value < 0;
  const absValue = Math.abs(value);

  let formatted: string;
  if (absValue >= 1_000_000) {
    formatted = `${(absValue / 1_000_000).toFixed(roundDigits)}M`;
  } else if (absValue >= 1_000) {
    formatted = `${(absValue / 1_000).toFixed(roundDigits)}k`;
  } else {
    formatted = absValue.toString();
  }

  return isNegative ? `-${formatted}` : formatted;
};

export const generateGreenVariants = (count: number): string[] => {
  const colors: string[] = [];
  const lightnessStep = 30 / count; // Spread lightness from 40% to 70%

  for (let i = 0; i < count; i++) {
    const lightness = 40 + i * lightnessStep;
    colors.push(`hsl(157, 16%, ${lightness}%)`); // Hue 140 = green
  }

  return colors;
};

export function getAgeFromISODateUTC(dateString: string): number {
  const birthDate = new Date(dateString);
  const now = new Date();

  let age = now.getUTCFullYear() - birthDate.getUTCFullYear();

  const hasHadBirthdayThisYear =
    now.getUTCMonth() > birthDate.getUTCMonth() ||
    (now.getUTCMonth() === birthDate.getUTCMonth() &&
      now.getUTCDate() >= birthDate.getUTCDate());

  if (!hasHadBirthdayThisYear) {
    age--;
  }

  return Math.max(age, 0);
}

export const formatPrice = (amount: number, currency: string) => {
  if (amount == null) return "-";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency ?? "INR",
  }).format(amount);
};

export const splitWords = (text: string) =>
  text.replace(/([a-z])([A-Z])/g, "$1 $2");

export const msToSeconds = (milliseconds: any, decimals = 1) => {
  return Number((milliseconds / 1000).toFixed(decimals));
};


export const getStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "pending":
      return "#fbc622";
    case "active":
    case "completed":
    case "approved":
    case "success":
      return "#22c55e";
    case "inactive":
    case "rejected":
    case "failed":
      return "#df1b41";
    case "review":
      return "#375fff";
    default:
      return "#002e77";
  }
};

export const getTimeAgo = (dateString: string): string => {
  if (!dateString) return "";
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
};